import { Flex, Span, Spinner } from "@goodfill/ui-components";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { DepositHistoryTable } from "./DepositHistoryTable";
import {
  Currency,
  TransactionType,
  doListTransactionsByBalanceAccountSortedByOrder,
} from "@goodfill/models-typescript";
import { API } from "aws-amplify";
import { PresentedDepositHistoryTransaction } from "./PresentedDepositHistoryTransaction";
import {
  defaultPortfolioDepositHistoryFilterConstraints,
  portfolioDepositHistoryFilters,
} from "./filter/PortfolioDepositHistoryFilter";
import { PortfolioDepositHistoryFilterComponent } from "./filter/PortfolioDepositHistoryFilterComponent";
import { useRecoilValue } from "recoil";
import { userDataState } from "../../../../state";
import { applyFilters } from "../../../../filters/filters";

const BIG_LIMIT = 10000;
const DEPOSIT_OR_WITHDRAWAL = [TransactionType.Deposit, TransactionType.Withdrawal];

export const DepositHistorySection = () => {
  const currency = Currency.USD;
  const userData = useRecoilValue(userDataState);

  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState<PresentedDepositHistoryTransaction[]>([]);
  const [filterConstraints, setFilterConstraints] = useState(defaultPortfolioDepositHistoryFilterConstraints);

  const balanceAccount = userData?.getBalanceAccount(currency);

  if (balanceAccount === null || balanceAccount === undefined) {
    throw new Error(`Missing balanceAccount for currency ${currency}`);
  }

  const init = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await doListTransactionsByBalanceAccountSortedByOrder(API, balanceAccount.id, BIG_LIMIT);
      setTransactions(
        result
          .filter((transaction) => DEPOSIT_OR_WITHDRAWAL.includes(transaction.type))
          .map((transaction) => ({
            transactedAt: transaction.transactedAt,
            amountChange: transaction.amountChange,
            type: transaction.type,
            source: transaction.source,
          })),
      );
    } catch (e) {
      console.error(e);
      toast.error(`Loading transactions failed ${e}`);
    } finally {
      setIsLoading(false);
    }
  }, [balanceAccount, DEPOSIT_OR_WITHDRAWAL]);

  useEffect(() => {
    init();
  }, [init]);

  const filteredRows = applyFilters(transactions, portfolioDepositHistoryFilters, filterConstraints);

  return (
    <Flex direction="col" verticalMargin={2} horizontalMargin={8} horizontalPadding={8} gap={8} width="w-full">
      <Flex direction="row" items="center" justify="between" verticalMargin={4}>
        <Span font="semibold" size="lg">
          Deposit
        </Span>
      </Flex>

      <PortfolioDepositHistoryFilterComponent
        constraints={filterConstraints}
        onConstraintsChange={setFilterConstraints}
      />

      {isLoading ? <Spinner /> : <DepositHistoryTable transactions={filteredRows} />}
    </Flex>
  );
};
